import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  PlayCircle as PlayCircleIcon,
} from "@mui/icons-material";

interface ChecklistItem {
  label: string;
  done: boolean;
  onClick: () => void;
  videoPath?: string;
  videoTitle?: string;
}

interface GettingStartedChecklistProps {
  items: ChecklistItem[];
  onPlayVideo?: (videoPath: string, title: string) => void;
  vertical?: boolean;
  sx?: Record<string, unknown>;
}

const GettingStartedChecklist: React.FC<GettingStartedChecklistProps> = ({
  items,
  onPlayVideo,
  vertical = false,
  sx,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        p: { xs: 2.5, sm: vertical ? 4 : 3 },
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: vertical
          ? "column"
          : { xs: "column", sm: "row" },
        alignItems: "center",
        textAlign: vertical ? "center" : undefined,
        gap: vertical ? 3 : 2,
        ...sx,
      }}
    >
      {/* Image */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "center",
          mb: vertical ? 0 : undefined,
        }}
      >
        <Box
          component="img"
          src="/home/getstarted.webp"
          alt="Get started"
          loading="lazy"
          decoding="async"
          sx={{
            width: vertical
              ? { xs: 180, sm: 220, md: 260 }
              : { xs: 140, sm: 170, md: 200 },
            height: "auto",
            objectFit: "contain",
          }}
        />
      </Box>

      {/* Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          flex: vertical ? undefined : 1,
          width: vertical ? "100%" : undefined,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          Getting Started
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
          Complete these steps to get the most out of Noticeboard:
        </Typography>

        {/* Checklist */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, textAlign: "left" }}>
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderRadius: 1,
              }}
            >
              <Box
                onClick={item.onClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: "pointer",
                  flex: 1,
                  "&:hover": { bgcolor: "action.hover" },
                  transition: "background-color 0.2s ease",
                  borderRadius: 1,
                  py: 0.5,
                  px: 0.5,
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: item.done ? "success.main" : "action.disabled",
                    fontSize: 18,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: item.done ? "text.primary" : "text.secondary",
                    flex: 1,
                  }}
                >
                  {item.label}
                </Typography>
                {item.done && (
                  <Typography variant="caption" sx={{ color: "success.main", fontWeight: 500, fontSize: "0.7rem" }}>
                    Done
                  </Typography>
                )}
              </Box>
              {item.videoPath && onPlayVideo && (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlayVideo(item.videoPath!, item.videoTitle || "Tutorial");
                  }}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    color: "primary.main",
                    "&:hover": { color: "primary.dark" },
                    transition: "color 0.2s ease",
                  }}
                >
                  <PlayCircleIcon sx={{ fontSize: 24 }} />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default GettingStartedChecklist;
